import { useEffect, useState } from "react";
import { CarDocDates } from "@/components/admin/CarDocDates";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { useApi, errMsg } from "@/lib/api";
import { ANGLES } from "@/lib/brand";
import { PhotoUploadButton } from "@/components/admin/PhotoUploadButton";

const EMPTY = {
  brand: "", model: "", variant: "", year: 2024, type: "Hatchback", transmission: "Manual", fuel: "Petrol", seats: 5,
  color: "", city: "Bengaluru", price_per_hour: 90, deposit: 3000, km_per_day: 300, extra_km_rate: 10,
  mileage: "", boot: "", features: [], description: "", active: true, extra_images: [],
  insurance_expiry: "", puc_expiry: "", permit_expiry: "", service_due: "",
  images: { front: "", back: "", driver: "", passenger: "", interior: "" },
};
const OPTS = {
  type: ["Hatchback", "Sedan", "SUV", "MUV", "Off-road"],
  transmission: ["Manual", "Automatic"],
  fuel: ["Petrol", "Diesel", "Electric", "CNG", "Hybrid"],
};
const NUMS = ["year", "seats", "price_per_hour", "deposit", "km_per_day", "extra_km_rate"];
const TEXTS = ["brand", "model", "variant", "color", "city", "mileage", "boot"];
const inputCls = "mt-1 w-full rounded-md border border-white/10 bg-[#0A0A0A] px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#FF5A00]";

export function CarFormDialog({ open, onOpenChange, car, draft, onSaved }) {
  const api = useApi();
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) setF(car ? { ...EMPTY, ...car } : draft ? { ...EMPTY, ...draft } : EMPTY);
  }, [open, car, draft]);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const urlsOf = (c) => [...Object.values(c?.images || {}), ...(c?.extra_images || [])].filter((u) => u?.startsWith("/api/car-photos/"));
  const cancel = (v) => {
    if (!v) {
      const keep = new Set(urlsOf(car));
      const unused = [...urlsOf(f), ...urlsOf(draft)].filter((u) => !keep.has(u));
      if (unused.length) api.post("/admin/car-photos/discard", { urls: [...new Set(unused)] }).catch(() => {});
    }
    onOpenChange(v);
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    const { id, name, _id, host_email, host_share_pct, rating_avg, rating_count, import_meta, ...body } = f;
    NUMS.forEach((k) => (body[k] = Number(body[k])));
    try {
      if (car) await api.put(`/admin/cars/${car.id}`, body);
      else await api.post("/admin/cars", body);
      toast.success(car ? "Car updated" : "Car added");
      onSaved();
      onOpenChange(false);
    } catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={cancel}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto" data-testid="car-form-dialog">
        <DialogHeader><DialogTitle>{car ? `Edit ${car.name}` : "Add a car"}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-6">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            {TEXTS.map((k) => (
              <label key={k} className="text-xs capitalize text-neutral-400">{k}
                <input required={["brand", "model", "city"].includes(k)} data-testid={`car-form-${k}`} value={f[k]} onChange={(e) => set(k, e.target.value)} className={inputCls} />
              </label>
            ))}
            {Object.entries(OPTS).map(([k, opts]) => (
              <label key={k} className="text-xs capitalize text-neutral-400">{k}
                <select data-testid={`car-form-${k}`} value={f[k]} onChange={(e) => set(k, e.target.value)} className={inputCls}>
                  {opts.map((o) => <option key={o}>{o}</option>)}
                </select>
              </label>
            ))}
            {NUMS.map((k) => (
              <label key={k} className="text-xs text-neutral-400">{k.replace(/_/g, " ")}
                <input required type="number" min="0" data-testid={`car-form-${k}`} value={f[k]} onChange={(e) => set(k, e.target.value)} className={inputCls} />
              </label>
            ))}
          </div>
          <label className="block text-xs text-neutral-400">Features (comma separated)
            <input data-testid="car-form-features" value={f.features.join(", ")} onChange={(e) => set("features", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))} className={inputCls} />
          </label>
          <label className="block text-xs text-neutral-400">Description
            <textarea rows={3} data-testid="car-form-description" value={f.description} onChange={(e) => set("description", e.target.value)} className={inputCls} />
          </label>
          <div>
            <p className="mb-3 text-sm font-semibold">Photos (upload or paste a link for all 5 angles)</p>
            <div className="grid gap-3 sm:grid-cols-2">
              {ANGLES.map((a) => (
                <div key={a.key} className="flex gap-3 rounded-md border border-white/10 p-2">
                  <div className="h-16 w-24 shrink-0 overflow-hidden rounded bg-white/5">
                    {f.images[a.key] && <img src={f.images[a.key]} alt={a.label} className="h-full w-full object-cover" />}
                  </div>
                  <label className="flex-1 text-xs text-neutral-400">{a.label}
                    <input required data-testid={`car-form-image-${a.key}`} value={f.images[a.key]} onChange={(e) => set("images", { ...f.images, [a.key]: e.target.value })} className={inputCls} placeholder="Upload or paste https://…" />
                    <PhotoUploadButton angle={a.key} onUploaded={(url) => setF((p) => ({ ...p, images: { ...p.images, [a.key]: url } }))} />
                  </label>
                </div>
              ))}
            </div>
            {f.extra_images.length > 0 && (
              <div className="mt-4" data-testid="car-form-extra-images">
                <p className="mb-2 text-xs text-neutral-400">More photos ({f.extra_images.length}) — shown after the 5 angles on the car page</p>
                <div className="flex flex-wrap gap-2">
                  {f.extra_images.map((src, i) => (
                    <div key={src} className="relative h-16 w-24 overflow-hidden rounded bg-white/5">
                      <img src={src} alt="" className="h-full w-full object-cover" />
                      <button type="button" onClick={() => set("extra_images", f.extra_images.filter((x) => x !== src))} data-testid={`car-form-extra-remove-${i}`}
                        className="absolute right-1 top-1 rounded bg-black/70 px-1.5 text-xs text-white">×</button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          <CarDocDates f={f} set={set} />
          <div className="flex items-center justify-between">
            <label className="flex items-center gap-2 text-sm"><Switch checked={f.active} onCheckedChange={(v) => set("active", v)} data-testid="car-form-active" /> Listed on website</label>
            <button disabled={busy} data-testid="car-form-save" className="rounded-md bg-[#FF5A00] px-6 py-2.5 text-sm font-semibold text-white disabled:opacity-50">{busy ? "Saving…" : "Save car"}</button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
